import React, { createContext, useContext, useState, useCallback } from 'react';
import tr from '../locales/tr/translation.json';
import en from '../locales/en/translation.json';
import fr from '../locales/fr/translation.json';

const resources = { tr, en, fr };

const LANGUAGE_KEY = 'monnaify_language';

const getDefaultLanguage = () => {
  // 1. Kayıtlı tercih
  const saved = localStorage.getItem(LANGUAGE_KEY);
  if (saved && resources[saved]) return saved;

  // 2. Tarayıcı dili
  const browserLang = navigator.language?.split('-')[0];
  if (browserLang && resources[browserLang]) return browserLang;

  // 3. Varsayılan
  return 'tr';
};

const I18nContext = createContext(null);

// Nested key erişimi: "auth.login.title" -> translations.auth.login.title
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
};

export function I18nProvider({ children }) {
  const [language, setLanguageState] = useState(getDefaultLanguage);

  const setLanguage = useCallback((lang) => {
    if (resources[lang]) {
      setLanguageState(lang);
      localStorage.setItem(LANGUAGE_KEY, lang);
      document.documentElement.lang = lang;
    }
  }, []);

  // Çeviri fonksiyonu
  const t = useCallback((key, params) => {
    const translation = getNestedValue(resources[language], key);

    if (translation === undefined) {
      // Fallback: Türkçe'ye bak
      const fallback = getNestedValue(resources['tr'], key);
      if (fallback !== undefined) {
        return interpolate(fallback, params);
      }
      // Hiçbir şey bulunamazsa key'i döndür
      console.warn(`[i18n] Missing translation: ${key}`);
      return key;
    }

    return interpolate(translation, params);
  }, [language]);

  const value = {
    t,
    language,
    setLanguage,
    languages: [
      { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
      { code: 'en', name: 'English', flag: '🇬🇧' },
      { code: 'fr', name: 'Français', flag: '🇫🇷' }
    ]
  };

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

// {{variable}} şeklinde parametreleri değiştir
function interpolate(text, params) {
  if (!params || typeof text !== 'string') return text;
  return Object.entries(params).reduce((result, [key, value]) => {
    return result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
  }, text);
}

export function useTranslation() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useTranslation must be used within I18nProvider');
  }
  return context;
}

export default I18nContext;
